/**
 * Export helpers for the Gestionale Incassi & Pagamenti module
 * (Gruppo Impresa Suite).
 *
 * Conventions (Italian context):
 *  - CSV separator: ';' (so Italian Excel opens it correctly)
 *  - Decimal separator: ','
 *  - Date format: dd/MM/yyyy
 *  - File starts with UTF-8 BOM so Excel detects encoding
 *
 * Public API:
 *   exportRows({ rows, columns, sheetName, filenameBase, format })
 *   exportSheets({ sheets, filenameBase, format })   // multi-sheet (xlsx) or concatenated (csv)
 *   buildFilename(base, filters?)                   // appends date range / year if present
 */
import * as XLSX from "xlsx";

const BOM = "\uFEFF";

/* -------------------- Cell coercion -------------------- */

const toNumber = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "string" ? parseFloat(v) : Number(v);
  return Number.isFinite(n) ? n : null;
};

const toDate = (v) => {
  if (!v) return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  // ISO yyyy-mm-dd or full ISO timestamp
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

const pad2 = (n) => String(n).padStart(2, "0");

const formatDateIT = (d) =>
  `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;

const formatNumberIT = (n, decimals = 2) => {
  // 1234.5 -> "1.234,50"
  const fixed = n.toFixed(decimals);
  const [intPart, decPart] = fixed.split(".");
  const withThousands = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return decPart ? `${withThousands},${decPart}` : withThousands;
};

/* -------------------- Column definition -------------------- */
/**
 * column = {
 *   key: 'due_date',
 *   header: 'Scadenza',
 *   type: 'string' | 'number' | 'currency' | 'integer' | 'date' | 'percent',
 *   accessor?: (row) => value     // override key-based access
 *   decimals?: number             // for number/currency
 * }
 */

const resolveValue = (row, col) => {
  if (typeof col.accessor === "function") return col.accessor(row);
  return row?.[col.key];
};

/* -------------------- CSV -------------------- */

const csvEscape = (raw) => {
  const s = raw === null || raw === undefined ? "" : String(raw);
  if (/[";\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const csvCell = (row, col) => {
  const v = resolveValue(row, col);
  if (v === null || v === undefined || v === "") return "";
  switch (col.type) {
    case "date": {
      const d = toDate(v);
      return d ? formatDateIT(d) : csvEscape(v);
    }
    case "number":
    case "currency": {
      const n = toNumber(v);
      return n === null ? csvEscape(v) : formatNumberIT(n, col.decimals ?? 2);
    }
    case "integer": {
      const n = toNumber(v);
      return n === null ? csvEscape(v) : String(Math.round(n));
    }
    case "percent": {
      // value expected as 0..1; render as 12,3%
      const n = toNumber(v);
      return n === null ? csvEscape(v) : `${formatNumberIT(n * 100, col.decimals ?? 1)}%`;
    }
    default:
      return csvEscape(v);
  }
};

const buildCsv = (rows, columns) => {
  const header = columns.map((c) => csvEscape(c.header)).join(";");
  const body = rows
    .map((r) => columns.map((c) => csvCell(r, c)).join(";"))
    .join("\r\n");
  return `${header}\r\n${body}`;
};

/* -------------------- XLSX -------------------- */

const numberFormatFor = (type, decimals = 2) => {
  if (type === "currency") return decimals === 0 ? '#,##0\\ "€"' : '#,##0.00\\ "€"';
  if (type === "number") return decimals === 0 ? "#,##0" : `#,##0.${"0".repeat(decimals)}`;
  if (type === "integer") return "0";
  if (type === "percent") return `0.${"0".repeat(decimals)}%`;
  if (type === "date") return "dd/mm/yyyy";
  return null;
};

const buildSheetAOA = (rows, columns) => {
  const header = columns.map((c) => c.header);
  const body = rows.map((r) =>
    columns.map((c) => {
      const v = resolveValue(r, c);
      if (v === null || v === undefined || v === "") return null;
      switch (c.type) {
        case "date":
          return toDate(v);
        case "number":
        case "currency":
        case "integer":
        case "percent":
          return toNumber(v);
        default:
          return String(v);
      }
    }),
  );
  return [header, ...body];
};

const styleSheet = (ws, rows, columns) => {
  // Header bold + autofilter + cell formats + reasonable widths.
  const range = XLSX.utils.decode_range(ws["!ref"]);
  // Column widths (rough heuristic)
  ws["!cols"] = columns.map((c) => {
    const headerLen = (c.header || "").length;
    const sampleLen = Math.min(
      30,
      rows.reduce((m, r) => {
        const v = resolveValue(r, c);
        const s = v === null || v === undefined ? "" : String(v);
        return Math.max(m, s.length);
      }, 0),
    );
    return { wch: Math.max(10, headerLen + 2, sampleLen + 2) };
  });
  // Auto-filter on data range
  ws["!autofilter"] = { ref: ws["!ref"] };
  // Apply number/date formats column by column
  columns.forEach((c, idx) => {
    const z = numberFormatFor(c.type, c.decimals);
    if (!z) return;
    for (let R = 1; R <= range.e.r; R += 1) {
      const cell = ws[XLSX.utils.encode_cell({ r: R, c: idx })];
      if (cell && cell.v !== null && cell.v !== undefined) {
        cell.z = z;
        if (c.type === "date" && cell.v instanceof Date) cell.t = "d";
      }
    }
  });
};

/* -------------------- Filename helpers -------------------- */

const sanitize = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/[àáâã]/g, "a")
    .replace(/[èéê]/g, "e")
    .replace(/[ìí]/g, "i")
    .replace(/[òóô]/g, "o")
    .replace(/[ùú]/g, "u")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

export const buildFilename = (base, filters = {}) => {
  const parts = [sanitize(base)];
  if (filters.year) {
    parts.push(String(filters.year));
  } else if (filters.date_from && filters.date_to) {
    parts.push(filters.date_from);
    parts.push(filters.date_to);
  } else if (filters.date_from) {
    parts.push(`dal_${filters.date_from}`);
  } else if (filters.date_to) {
    parts.push(`al_${filters.date_to}`);
  } else {
    const today = new Date();
    parts.push(
      `${today.getFullYear()}${pad2(today.getMonth() + 1)}${pad2(today.getDate())}`,
    );
  }
  return parts.filter(Boolean).join("_");
};

/* -------------------- Browser download -------------------- */

const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  // Keep the anchor out of the React-managed tree to avoid races with portals
  // closing/opening (e.g. Radix Dialog) which previously caused intermittent
  // "Failed to execute 'insertBefore' on 'Node'" errors.
  a.style.display = "none";
  document.body.appendChild(a);
  try {
    a.click();
  } finally {
    // Defensive removal: only if the anchor is still attached.
    if (a.parentNode) {
      a.parentNode.removeChild(a);
    }
    setTimeout(() => URL.revokeObjectURL(url), 200);
  }
};

/* -------------------- Public API -------------------- */

/**
 * Export a single tabular dataset.
 *
 * params:
 *   rows         array of objects
 *   columns      array of column defs (see above)
 *   sheetName    used as XLSX sheet name (truncated to 31 chars)
 *   filenameBase string (no extension)
 *   format       'csv' | 'xlsx'
 */
export const exportRows = ({
  rows,
  columns,
  sheetName = "Foglio1",
  filenameBase,
  format,
}) => {
  if (format === "csv") {
    const csv = buildCsv(rows || [], columns);
    const blob = new Blob([BOM + csv], { type: "text/csv;charset=utf-8" });
    triggerDownload(blob, `${filenameBase}.csv`);
    return;
  }
  // xlsx
  const wb = XLSX.utils.book_new();
  const aoa = buildSheetAOA(rows || [], columns);
  const ws = XLSX.utils.aoa_to_sheet(aoa, { cellDates: true });
  styleSheet(ws, rows || [], columns);
  XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31));
  const out = XLSX.write(wb, { type: "array", bookType: "xlsx" });
  triggerDownload(
    new Blob([out], { type: "application/octet-stream" }),
    `${filenameBase}.xlsx`,
  );
};

/**
 * Export multiple sheets in one file.
 *
 * params:
 *   sheets        [{ name, rows, columns }]
 *   filenameBase  string
 *   format        'csv' | 'xlsx'
 *
 * In CSV mode, sheets are concatenated with a blank-line + section title.
 */
export const exportSheets = ({ sheets, filenameBase, format }) => {
  if (format === "csv") {
    const blocks = sheets.map((s) => {
      const title = `### ${s.name}`;
      const csv = buildCsv(s.rows || [], s.columns);
      return `${title}\r\n${csv}`;
    });
    const csv = blocks.join("\r\n\r\n");
    const blob = new Blob([BOM + csv], { type: "text/csv;charset=utf-8" });
    triggerDownload(blob, `${filenameBase}.csv`);
    return;
  }
  const wb = XLSX.utils.book_new();
  sheets.forEach((s) => {
    const aoa = buildSheetAOA(s.rows || [], s.columns);
    const ws = XLSX.utils.aoa_to_sheet(aoa, { cellDates: true });
    styleSheet(ws, s.rows || [], s.columns);
    XLSX.utils.book_append_sheet(wb, ws, (s.name || "Foglio").slice(0, 31));
  });
  const out = XLSX.write(wb, { type: "array", bookType: "xlsx" });
  triggerDownload(
    new Blob([out], { type: "application/octet-stream" }),
    `${filenameBase}.xlsx`,
  );
};
